/**
 * One act on one client's ledger (T-C3 … T-C6) — the draft, its one send, and what the answer
 * leaves: the booking screen's Rozliczenia tab and the client card's „Rozliczenie" band both open
 * their acts through this.
 *
 * ── The request id ─────────────────────────────────────────────────────────────────────────────
 * Minted as the dialog opens (`openLedgerAct`) and carried by every edit and every resend. A
 * refusal as already used closes the act as saved; a send with no answer LOCKS the draft — the
 * draft that went out is the draft — and the only way on is to send it again under the same id,
 * which the server converges onto the entry it may already hold (R14, `ledgerRefusal`).
 *
 * ── A save ─────────────────────────────────────────────────────────────────────────────────────
 * The answer IS the ledger as it now stands (R15): it seeds the first page of the history (the
 * older pages already loaded stay) and the summary's row before the dialog closes, so an act opened
 * next reads the new standing while the read after the write is still out.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  postCreditLedgerCustomerIdAdjustment,
  postCreditLedgerCustomerIdForgiveness,
  postCreditLedgerCustomerIdGrant,
  postCreditLedgerCustomerIdSettlement,
} from '~/shared/api/generated/endpoints/credit-ledger/credit-ledger';
import type { TrainWithMeModulesBookingDtosResponsesCreditLedgerResponse as CreditLedger } from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';

import {
  creditSummaryQueryOptions,
  customerLedgerPagesQueryOptions,
  readBookingAgain,
} from '../api/creditQueries';
import {
  ledgerActBody,
  ledgerActVerdict,
  ledgerRefusal,
  openLedgerAct,
  type LedgerAct,
  type LedgerActDraft,
  type LedgerField,
  type LedgerStanding,
} from '../model/ledgerAct';

export interface LedgerActState {
  /** The act being drafted, or `null` while no dialog is open. */
  readonly draft: LedgerActDraft | null;
  readonly open: (act: LedgerAct) => void;
  readonly edit: (draft: LedgerActDraft) => void;
  readonly send: () => void;
  readonly close: () => void;
  readonly sending: boolean;
  /** The last send got no answer: the draft is locked and goes again only as it went. */
  readonly unanswered: boolean;
  /** The last refusal: its words, and the field it is about when the server named one. */
  readonly refusal: { readonly text: string; readonly field: LedgerField | null } | null;
}

const POST: Readonly<
  Record<
    LedgerAct,
    (customerId: number, body: ReturnType<typeof ledgerActBody>) => Promise<CreditLedger>
  >
> = {
  grant: (id, body) => postCreditLedgerCustomerIdGrant(id, body),
  settlement: (id, body) => postCreditLedgerCustomerIdSettlement(id, body),
  adjustment: (id, body) => postCreditLedgerCustomerIdAdjustment(id, body),
  forgiveness: (id, body) => postCreditLedgerCustomerIdForgiveness(id, body),
};

/**
 * @param standing where the client stands as the caller's own read says — what a send is judged
 *   against, so the dialog and the act can never disagree about what may go out.
 */
export function useLedgerAct(
  customerId: number,
  standing: LedgerStanding,
  announce: (message: string) => void,
): LedgerActState {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<LedgerActDraft | null>(null);
  const [refusal, setRefusal] = useState<LedgerActState['refusal']>(null);
  const [unanswered, setUnanswered] = useState(false);
  const pagesKey = customerLedgerPagesQueryOptions(customerId).queryKey;

  const alreadySaved = () => {
    setUnanswered(false);
    setDraft(null);
    setRefusal(null);
    announce(t('web:booking.credits.act.alreadySaved'));
    void readBookingAgain(queryClient);
  };

  const act = useMutation({
    mutationFn: (sent: LedgerActDraft) => POST[sent.act](customerId, ledgerActBody(sent)),
    onMutate: () => {
      setRefusal(null);
    },
    onSuccess: (answer, sent) => {
      queryClient.setQueryData(pagesKey, (old) =>
        old === undefined
          ? { pages: [answer], pageParams: [undefined] }
          : { pages: [answer, ...old.pages.slice(1)], pageParams: old.pageParams },
      );
      queryClient.setQueryData(creditSummaryQueryOptions().queryKey, (summary) =>
        summary === undefined
          ? summary
          : {
              ...summary,
              clients: (summary.clients ?? []).map((row) =>
                row.customerId === customerId
                  ? {
                      ...row,
                      balance: answer.balance ?? row.balance,
                      sessionsOwed: answer.sessionsOwed ?? row.sessionsOwed,
                    }
                  : row,
              ),
            },
      );
      setUnanswered(false);
      setDraft(null);
      announce(
        sent.act === 'grant' || sent.act === 'settlement'
          ? t(`web:booking.trainer.credits.sheet.${sent.act}.saved`, { count: sent.amount })
          : t(`web:booking.trainer.credits.sheet.${sent.act}.saved`),
      );
      void readBookingAgain(queryClient);
    },
    onError: (error) => {
      const refused = ledgerRefusal(
        error instanceof ApiError
          ? { status: error.status, code: error.code, field: error.problem?.field }
          : null,
      );
      if (refused.kind === 'alreadySaved') {
        alreadySaved();
        return;
      }
      if (refused.kind === 'unreached') {
        setUnanswered(true);
        setRefusal({ text: t('web:booking.credits.act.unanswered'), field: null });
        return;
      }
      setUnanswered(false);
      setRefusal({ text: t(refused.key), field: refused.field });
      if (refused.refetch) void readBookingAgain(queryClient);
    },
  });

  return {
    draft,
    open: (chosen) => {
      setRefusal(null);
      setDraft(openLedgerAct(chosen, crypto.randomUUID()));
    },
    edit: (next) => {
      // Locked while an unanswered send may have landed: the draft that went out is the draft.
      if (!unanswered) setDraft(next);
    },
    send: () => {
      if (draft === null || act.isPending) return;
      // A resend after no answer is not judged again: the draft passed once, and the balance it is
      // judged against may already include it. The server converges a same-id resend.
      if (!unanswered && ledgerActVerdict(draft, standing).blockedBy !== null) return;
      act.mutate(draft);
    },
    close: () => {
      if (act.isPending) return;
      if (unanswered) {
        // Nothing is kept to send again: say what may have happened, and read what did.
        setUnanswered(false);
        announce(t('web:booking.credits.act.mayHaveLanded'));
        void readBookingAgain(queryClient);
      }
      setDraft(null);
      setRefusal(null);
    },
    sending: act.isPending,
    unanswered,
    refusal,
  };
}
